import cx from 'clsx';
import React, { FunctionComponent, MouseEvent } from 'react';
import styles from './PostItems.module.scss';
import utilityStyles from 'styles/global/utility.module.scss';
import { Block, BlockContent, BlockHeader, Image, LinkWrapper, Link, Typography, Card, Loader } from 'components/elements';
import { getArticleFeaturedImage, getArticleTerms } from 'utils';
import { WPAcfBasic } from 'types/acf';
import { WPSimpleArticle, WPArticleEmbedded } from 'types/articles';
import { WpFeaturedMediaImageTypes } from 'types/terms';
import { useIntl } from 'react-intl';
import messages from 'messages/base-pl';
import DOMPurify from 'dompurify';

type AvailableItems = 1 | 2 | 3 | 4;

type MobileSizes = `mobile${AvailableItems}`;
type TabletSizes = `tablet${AvailableItems}`;
type DesktopSizes = `desktop${AvailableItems}`;

interface OwnProps {
  mobile: AvailableItems;
  tablet: AvailableItems;
  desktop: AvailableItems;
  posts: WPSimpleArticle<WPAcfBasic>[];
  category: string;
  header?: string;
  showTitle?: boolean;
  showExcerpt?: boolean;
  showCategory?: boolean;
  showLink?: boolean;
  showImage?: boolean;
  linkLabel?: string;
  length?: number;
  showMore?: boolean;
  loadMore?: boolean;
  loading?: boolean;
  onLoadMore?: () => void;
  seeMoreLabel?: string;
}

type PostItemsProps = JSX.IntrinsicElements['div'] & OwnProps;

const PostItems: FunctionComponent<PostItemsProps> = props => {
  const {
    mobile,
    tablet,
    desktop,
    posts = [],
    category,
    header,
    linkLabel,
    showLink = true,
    showExcerpt = true,
    showImage = true,
    showTitle = true,
    showCategory = false,
    length = 4,
    showMore = false,
    loadMore = false,
    onLoadMore = () => {},
    loading = false,
    children,
    seeMoreLabel,
    ...rest
  } = props;
  const { formatMessage } = useIntl();

  const mobileSize = `mobile${mobile}` as MobileSizes;
  const tabletSize = `tablet${tablet}` as TabletSizes;
  const desktopSize = `desktop${desktop}` as DesktopSizes;

  const postItemClassNames = cx({
    [styles.item]: true,
    [styles[mobileSize]]: true,
    [styles[tabletSize]]: true,
    [styles[desktopSize]]: true,
  });

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onLoadMore();
  };

  const renderImage = (featured_media_src_url: string, _embedded: WPArticleEmbedded<WPAcfBasic> | undefined) => {
    const featuredImage = getArticleFeaturedImage(_embedded);
    const selectedFeaturedImage = featuredImage[0];

    if (!selectedFeaturedImage || !selectedFeaturedImage?.media_details) {
      return null;
    }

    const sizes = selectedFeaturedImage?.media_details.sizes || {};
    const srcSetArray = Object.keys(sizes).map(
      size => `${sizes[size as WpFeaturedMediaImageTypes].source_url} ${sizes[size as WpFeaturedMediaImageTypes].width}w`,
    );

    return (
      <Image
        className={styles.image}
        sizes={`(max-width: 1023px) ${100 / mobile}vw, (min-width: 1024px) and (max-width: 1365px) ${100 / tablet}vw, ${100 / desktop}vw`}
        srcSet={srcSetArray.join(',')}
        src={featured_media_src_url}
        height={featuredImage[0]?.media_details.height}
        width={featuredImage[0]?.media_details.width}
      />
    );
  };

  return (
    <Block id={`post-items-${category}`} data-module="post-items" {...rest} style={{ minHeight: '50vh' }}>
      <Loader loading={loading} />
      <BlockContent direction="row">
        {header && <BlockHeader>{header}</BlockHeader>}
        {!loading &&
          posts.slice(0, length).map(({ title, excerpt, featured_media_src_url, slug, _embedded }, index) => {
            const { category: categories } = getArticleTerms(_embedded);
            return (
              <article key={`post-${category}-item-${index}`} className={postItemClassNames}>
                <Card>
                  {showImage && renderImage(featured_media_src_url, _embedded)}
                  <div className={styles.content}>
                    {showTitle && (
                      <Typography
                        mobile="new_neue_20_h2"
                        tablet="new_neue_20_h2"
                        desktop="new_neue_20_h2"
                        as="h2"
                        className={styles.title}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title.rendered) }}
                      />
                    )}
                    {showCategory && categories && (
                      <Typography mobile="new_neue_12" tablet="new_neue_12" desktop="new_neue_12" className={styles.category} as="div">
                        {categories.map(category => category.name).join(', ')}
                      </Typography>
                    )}
                    {showExcerpt && (
                      <Typography
                        mobile="new_ibm_regular_16"
                        tablet="new_ibm_regular_16"
                        desktop="new_ibm_regular_16"
                        className={styles.description}
                        as="div"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(excerpt.rendered) }}
                      />
                    )}
                    {showLink && (
                      <div className={styles.link}>
                        <Link href={`/${category}/${slug}`}>
                          <span aria-hidden="true">{linkLabel || formatMessage(messages.show_post)}</span>
                          <span className={utilityStyles.visuallyHidden}>{DOMPurify.sanitize(title.rendered)}</span>
                        </Link>
                      </div>
                    )}
                  </div>
                  {showLink && (
                    <LinkWrapper
                      className={styles.overlayLink}
                      aria-hidden="true"
                      tabIndex={-1}
                      aria-label={linkLabel || formatMessage(messages.show_post)}
                      href={`/${category}/${slug}`}
                    />
                  )}
                </Card>
              </article>
            );
          })}
        {children && children}
        {loadMore && posts.length >= length && (
          <div className={styles.showMore}>
            <Link onClick={handleClick} className={styles.link} href="#">
              {formatMessage(messages.load_more)}
            </Link>
          </div>
        )}
        {showMore && (
          <div className={styles.showMore}>
            <Link className={styles.link} href={`/${category}`}>
              {seeMoreLabel || linkLabel || formatMessage(messages.show_posts)}
            </Link>
          </div>
        )}
      </BlockContent>
    </Block>
  );
};

export default PostItems;
